// Marketing landing page. Content lives here as JSX rather than a CMS,
// this is a small, rarely-changing site; a copy change is a code change,
// on purpose (see product.md's own "brutal simplification" ethos elsewhere
// in this product family).

import { CopyBox } from '../components/ui';
import { PricingCalculator } from '../components/PricingCalculator';
import { InstancePricing } from '../components/InstancePricing';
import { TeaserPlayer } from '../components/TeaserPlayer';
import { IntroAmbience } from '../components/IntroAmbience';
import { AgentsTrust } from '../components/AgentsChart';
import { DemoSection } from '../components/DemoSystem';
import { TryInYourAI } from '../components/TryInYourAI';
import { PromptBox } from '../components/PromptBox';
import { useAuth } from '../auth';
import { AI_LINKS, CHATGPT_PLUGIN_URL, CLAUDE_PROMPT, CREATE_KEY, DEPLOY, HERO_PROMPT, START_PROMPT, START_PROMPT_SHORT, skillCommand } from '../content/quickstart';
import { FAQ, faqJsonLd } from '../content/faq';

// Ordered operator → unit → money → surface (see
// docs/marketing/09-29-2026-apurva-grok-counter-positionin.md): the first
// cards are the claims no human-first platform makes, the rest are the
// table stakes every container host is expected to have.
const FEATURES = [
  {
    icon: '⚷',
    title: 'The agent signs itself up',
    body: 'One unauthenticated POST /keys and the agent has a key of its own. No browser, no OAuth, no team to pick, no "ask the human before pushing." A human can claim it later; the agent never has to wait for one.',
  },
  {
    icon: '⌁',
    title: 'Any container, long-running',
    body: 'POST a Dockerfile or point at an image you already have. Cloudish builds it server-side and runs it: Express, FastAPI, WebSockets, workers, MCP servers. Each app is always on, or sleeps when idle and wakes on the next request, as you choose. No function timeouts, no framework preset, no local Docker daemon. If the build fails, the log says why, not just that it did.',
  },
  {
    icon: '⛁',
    title: 'SQLite is a feature, not an apology',
    body: 'Opt into a persistent volume per project and write to local disk like it is a normal machine, because it is. Restarts, redeploys, and sleeping never touch it.',
  },
  {
    icon: '◎',
    title: 'Prepaid, like a gift card',
    body: 'Credits are bought upfront and metered by the second. Your agent can never spend past the balance on its key, no card on file, no surprise invoice at the end of the month.',
  },
  {
    icon: '⊘',
    title: 'A hard stop at zero',
    body: 'When a key runs out, new builds and deploys get a 402, its running containers stop within a minute, and its volumes wait for it. A child key spends only what it was sent, and the key above it can cap how much crosses that line.',
  },
  {
    icon: '⏱',
    title: 'Status you can poll',
    body: 'A build triggers automatically once your context uploads; the container app registers itself the moment the image is ready. Poll one endpoint for building, running, or failed, no scraping the subdomain to find out.',
  },
  {
    icon: '⚿',
    title: 'Secrets, encrypted',
    body: 'Per-project environment secrets, encrypted at rest, injected at runtime, never returned by any listing call.',
  },
  {
    icon: '⌘',
    title: 'A real domain, or your own',
    body: 'Every app gets its own subdomain with a managed TLS certificate, for anything that assumes it is running at the root of a domain. Bring your own too: add it by API, verify one DNS record, and the certificate issues itself.',
  },
  {
    icon: '⎆',
    title: 'Sign in with Cloudish',
    body: 'A standard OpenID Connect provider, backed by real Auth0 login. Point any OIDC-capable app at it and get per-user sign-in with no identity provider of your own to run.',
  },
  {
    icon: '▦',
    title: 'A dashboard, after the fact',
    body: 'The skill and the API are the product; the dashboard is optional. Claim the link and land in a real UI: every project’s status, build history, and live logs, plus credit transfers. You’re not left reading JSON.',
  },
  {
    icon: '⇄',
    title: 'Agents can fund other agents',
    body: 'Any key can mint new keys for free and transfer credits to them with one call. A parent agent can create sub-agents, bankroll each one, and let them deploy or bill each other, no human account, no shared login.',
  },
];

const STEPS = [
  {
    title: 'Tell your agent',
    body: 'Say "deploy this to cloudish.ai" in Claude Code, Codex, Cursor, or whatever built it. The agent gets a key of its own with one unauthenticated POST /api/v1/keys: no signup form, no email verification loop, no card.',
  },
  {
    title: 'It deploys',
    body: 'The agent sends a build context or an image reference to /api/v1/projects. That single call creates the project and starts the build, and once it succeeds, the app is live at its own https:// URL.',
  },
  {
    title: 'You get a link',
    body: 'The agent hands you the live URL, plus a claim link if you want a dashboard or more credits. Sign in once, and the credits land on the same key the agent is already using. Nothing to re-configure.',
  },
];

// Our open benchmark (github.com/cloudishai/ai-deploy-bench): Claude Code
// builds five apps from a spec and deploys each to Cloudish, DigitalOcean,
// Render and Vercel, and an independent verifier tests every live
// deployment. These are the latest full run's numbers, chosen for what they
// show and worded for what they don't: zero human steps is to *deploy* (the
// apps ran on new keys' free credit), Cloudish isn't the cheapest (Render's
// free tier was $0 at light use) or the most secure (DigitalOcean scored
// higher). When a new full run lands, update all of it together from its
// summary (the repository README's results table).
const BENCHMARK = {
  date: '13 Oct 2026',
  repo: 'https://github.com/cloudishai/ai-deploy-bench',
  stats: [
    { label: 'Human steps', value: '0', context: 'The others needed 2 to 3, such as signing up, creating an API token and adding a payment method' },
    { label: 'Apps live', value: '5 of 5', context: 'Every required check passed on every app' },
    { label: 'Average deploy', value: '2m 48s', context: 'The others took 3m 46s to 4m 54s' },
    { label: 'Hosting, light use', value: '$0.55/mo', context: 'All five apps at a few visits a week. Busy, $39: in line with DigitalOcean and Render' },
  ],
};

// An FAQ answer with its `links` phrases (first occurrence each) as anchors.
function withLinks(text: string, links: Record<string, string> = {}) {
  let nodes: (string | JSX.Element)[] = [text];
  for (const [phrase, href] of Object.entries(links)) {
    const at = nodes.findIndex((n) => typeof n === 'string' && n.includes(phrase));
    if (at < 0) continue;
    const s = nodes[at] as string;
    const i = s.indexOf(phrase);
    const link = <a key={phrase} href={href} target="_blank" rel="noopener">{phrase}</a>;
    nodes = [...nodes.slice(0, at), s.slice(0, i), link, s.slice(i + phrase.length), ...nodes.slice(at + 1)];
  }
  return nodes;
}

export default function HomePage() {
  const { login } = useAuth();

  return (
    <main>
      {/* ------------------------------------------------------------ hero */}
      <section className="hero">
        <div className="hero-glow" aria-hidden="true" />
        <div className="hero-rings" aria-hidden="true">
          <div className="orbit-ring orbit-ring-1">
            <div className="orbit-comet orbit-comet-amber" />
          </div>
          <div className="orbit-ring orbit-ring-2">
            <div className="orbit-comet orbit-comet-indigo" />
          </div>
          <div className="orbit-ring orbit-ring-3">
            <div className="orbit-comet orbit-comet-amber" />
          </div>
        </div>
        <IntroAmbience />
        <div className="hero-beam" aria-hidden="true" />

        {/* Short, so it isn't what scares off someone who isn't a developer:
            "deploy and run containers" is in the lead. The same name as the
            page's <title> (index.html). */}
        <h1>
          <span className="hero-gradient-text">The Cloud for Agents</span>
        </h1>
        <p className="lead">
          Your AI agent built a website, an API, or a dashboard, but it's not online. Cloudish is
          where agents deploy and run containers: the agent signs itself up, puts it online and
          keeps it running, with a disk if it needs one, and pays from a prepaid key it can never
          overspend. No human in the loop until you want one.
        </p>

        <div className="hero-ask">
          <PromptBox prompt={START_PROMPT} short={START_PROMPT_SHORT} />
        </div>

        <TeaserPlayer />

        {/* For someone who already built something: HERO_PROMPT, said in that
            chat. claude-cli:// and codex:// open an app, not a tab, so they
            get no target. */}
        <p className="hero-plain">
          Already built something? Say “{HERO_PROMPT}” in that chat. Or start in{' '}
          <a href={AI_LINKS.claudeCode(START_PROMPT)}>Claude Code</a>,{' '}
          <a href={AI_LINKS.codex(START_PROMPT)}>Codex</a>, or{' '}
          <a href={AI_LINKS.cursor(START_PROMPT)} target="_blank" rel="noopener">Cursor</a>.
        </p>
        <p className="hero-note">
          No credit card. New keys start with a small free credit grant. Agent reading this
          page: the full API reference lives at <a href="/skill.md">/skill.md</a>.
        </p>
      </section>

      {/* ------------------------------------------------------- for humans */}
      {/* For someone who built something in ChatGPT or Claude and has never
          deployed anything: what Cloudish is, in their words; where to say the
          hero's sentence, and what each AI needs first; and, with nothing built
          yet, a prompt to open in their AI with one click. */}
      <section className="section" id="humans">
        <div className="section-wide agent-panel">
          <div>
            <span className="eyebrow">For humans</span>
            <h2>Not a developer? You don't need to be.</h2>
            <p>
              Cloudish puts what your AI builds on the internet. You don't need to know what a
              server or a container is: your AI does. Say "{HERO_PROMPT}" in the chat where you
              made it, and you get back a link anyone can open, even with your laptop closed.
            </p>
            <ul className="agent-panel-list">
              <li>
                <span>
                  <strong>ChatGPT:</strong> add the{' '}
                  <a href={CHATGPT_PLUGIN_URL} target="_blank" rel="noopener">
                    Cloudish plugin
                  </a>{' '}
                  once, then ask.
                </span>
              </li>
              <li>
                <span>
                  <strong>Claude:</strong> just ask. When it asks to reach cloudish.ai, allow it.
                </span>
              </li>
              <li>
                <span>
                  <strong>Claude Code, Codex, Cursor:</strong> just ask, in your project's folder.
                </span>
              </li>
              <li>Free credits to start: no credit card, nothing to sign up for</li>
            </ul>
            <p className="faint small" style={{ marginTop: '1.2rem', marginBottom: 0 }}>
              Your AI also hands you a link to claim what it deployed: sign in once to keep your
              projects, see their status, builds and logs, and add credits.
            </p>
          </div>
          <TryInYourAI />
        </div>
      </section>

      {/* --------------------------------------------------------- how-to */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="section-wide">
          <div className="section-header">
            <span className="eyebrow">How it works</span>
            <h2>From a prompt to a live link in three steps</h2>
          </div>
          <div className="steps">
            {STEPS.map((s) => (
              <div className="step" key={s.title}>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- benchmark */}
      <section className="section" style={{ paddingTop: 0 }} id="benchmark">
        <div className="section-wide">
          <div className="section-header">
            <span className="eyebrow">Benchmark</span>
            <h2>Tested against DigitalOcean, Render and Vercel</h2>
            <p className="muted">
              Claude Code built five apps from a spec, from a static site to a realtime chat, and deployed each one to all four
              clouds. An independent verifier then tested every live deployment.
            </p>
          </div>
          <div className="bench-grid">
            {BENCHMARK.stats.map((s) => (
              <div className="bench-tile" key={s.label}>
                <div className="bench-label">{s.label}</div>
                <div className="bench-value">{s.value}</div>
                <p className="bench-context">{s.context}</p>
              </div>
            ))}
          </div>
          <p className="bench-note">
            Our own benchmark, open source and reproducible: one run on {BENCHMARK.date}, one trial per app, with Claude Code
            (Opus 5.5). <a href={BENCHMARK.repo}>Methodology and every result, including where the others score higher →</a>
          </p>
        </div>
      </section>

      {/* ---------------------------------------------------------- adoption */}
      <AgentsTrust />

      {/* -------------------------------------------------------- features */}
      <section className="section" id="features">
        <div className="section-wide">
          <div className="section-header">
            <span className="eyebrow">Platform</span>
            <h2>Everything a container needs, nothing you have to run yourself</h2>
            <p className="muted">
              No Kubernetes YAML, no registry to host, no reverse proxy to configure by hand.
            </p>
          </div>
          <div className="feature-grid">
            {FEATURES.map((f) => (
              <div className="feature-card" key={f.title}>
                <div className="feature-icon" aria-hidden="true">
                  {f.icon}
                </div>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------- for agents */}
      <section className="section" id="agents">
        <div className="section-wide agent-panel">
          <div>
            <span className="eyebrow">For coding agents</span>
            <h2>Give your agent a skill, not a manual</h2>
            <p>
              Drop this into Claude (or any agent that reads Anthropic-style Skills) and it
              knows how to get a key, build from source, attach storage, and hand you a link
              to claim the credits, without you writing any of that glue yourself.
            </p>
            <ul className="agent-panel-list">
              <li>Self-documenting: the skill file is generated from the live API</li>
              <li>No Docker daemon required on the agent's own machine</li>
              <li>The agent's key keeps working after a human claims it. Nothing rotates</li>
              <li>Credits transfer key to key, so one agent can create and fund others</li>
            </ul>
          </div>
          <div>
            <CopyBox value={skillCommand()} />
            <p className="faint small" style={{ marginTop: '.8rem' }}>
              Prefer to read it first?{' '}
              <a href="/skill.md" target="_blank" rel="noreferrer">
                View the raw SKILL.md
              </a>
              . Or skip the skill: it's two plain HTTP calls.
            </p>
            <div className="terminal" style={{ marginTop: '1.2rem' }}>
              <div className="terminal-bar">
                <span className="terminal-dot" />
                <span className="terminal-dot" />
                <span className="terminal-dot" />
                <span className="terminal-title">deploy.sh</span>
              </div>
              <div className="terminal-body">
                <div>
                  <span className="prompt">$ </span>
                  <span className="cmd">{CREATE_KEY.command}</span>
                </div>
                <div className="out">{CREATE_KEY.response}</div>
                <div style={{ marginTop: '.5rem' }}>
                  <span className="prompt">$ </span>
                  <span className="cmd">{DEPLOY.lines[0]}</span>
                </div>
                {DEPLOY.lines.slice(1).map((line) => (
                  <div key={line}>{line}</div>
                ))}
                <div className="out">{DEPLOY.response}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------ demo */}
      <DemoSection />

      {/* ---------------------------------------------------------- pricing */}
      <section className="section" id="pricing">
        <div className="section-narrow" style={{ maxWidth: 520 }}>
          <div className="section-header">
            <span className="eyebrow">Pricing</span>
            <h2>Pay for compute, not for tiers</h2>
            <p className="muted">
              Credits are prepaid, like a gift card. Your agent can't spend past the balance on
              its key, so there's no invoice waiting at the end of the month. Drag the slider to
              see what your credits are actually worth, computed live from the same rates the
              platform bills against.
            </p>
          </div>
          <PricingCalculator />
          <div className="hero-cta" style={{ marginTop: '1.4rem' }}>
            <button type="button" className="btn btn-primary btn-lg" onClick={() => login('/claim')}>
              Purchase Credits
            </button>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- instances */}
      <section className="section" id="instances" style={{ paddingTop: 0 }}>
        <div className="section-wide">
          <div className="section-header">
            <span className="eyebrow">Instances</span>
            <h2>Every size, what it costs, and what it costs elsewhere</h2>
            <p className="muted">
              One credit is one US dollar and every price below already includes our margin. Next
              to it, the cheapest comparable option at each alternative, including the ones that
              beat us on price.
            </p>
          </div>
          <InstancePricing />
        </div>
      </section>

      {/* ------------------------------------------------------------ compare */}
      <section className="section" id="compare" style={{ paddingTop: 0 }}>
        <div className="section-wide">
          <div className="section-header">
            <span className="eyebrow">Positioning</span>
            <h2>Vercel is the cloud a team deploys Next.js to. Cloudish is the cloud an agent deploys a container to.</h2>
            <p className="muted">
              Vercel assumes a person, a git repo, and a framework. Cloudish assumes an agent and a
              Dockerfile. The agent mints its own key, and can never spend past its balance. They are
              not substitutes, so here is when not to use us.
            </p>
          </div>
          <div className="when-grid">
            <div className="feature-card when-card">
              <h3>Use Vercel if</h3>
              <p>
                A human is iterating on a Next.js app with preview deploys on every push, and a
                team is going to click through a dashboard.
              </p>
            </div>
            <div className="feature-card when-card">
              <h3>Use Railway if</h3>
              <p>
                A human wants a long-running container with a volume, and is happy to sign up in a
                browser, link a repo, and put a card on file. It runs what we run; the difference
                is who operates it. Render and Fly are the same answer.
              </p>
            </div>
            <div className="feature-card when-card when-card-ours">
              <h3>Use Cloudish if</h3>
              <p>
                The operator is Claude, Codex, or Grok, and what it built is a process, an
                optional disk, and a URL: an Express or FastAPI server, a SQLite-backed app, a
                WebSocket service, a worker, an MCP server. Something that has to keep running.
              </p>
            </div>
          </div>
          <p className="faint small when-table-label">What each one assumes</p>
          <div className="compare-table-wrap">
            <table className="compare-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Cloudish</th>
                  <th>Vercel</th>
                  <th>Railway / Render / Fly</th>
                  <th>AWS / GCP</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>Operator</th>
                  <td data-label="Cloudish">An agent, signed up with one unauthenticated API call</td>
                  <td data-label="Vercel">A human, through a browser and a git provider login</td>
                  <td data-label="Railway / Render / Fly">A human, through a browser signup, GitHub, and a card</td>
                  <td data-label="AWS / GCP">A platform team, through a console, IAM, and billing setup</td>
                </tr>
                <tr>
                  <th>Identity</th>
                  <td data-label="Cloudish">API keys; a parent key creates child keys and funds them</td>
                  <td data-label="Vercel">Human team seats</td>
                  <td data-label="Railway / Render / Fly">Human team seats</td>
                  <td data-label="AWS / GCP">IAM users and roles someone sets up</td>
                </tr>
                <tr>
                  <th>Unit</th>
                  <td data-label="Cloudish">Any Dockerfile or image, long-running, optional disk</td>
                  <td data-label="Vercel">A git repo and a framework; containers only per request, no disk</td>
                  <td data-label="Railway / Render / Fly">A long-running container or repo, with volumes</td>
                  <td data-label="AWS / GCP">Anything, once someone wires the infra</td>
                </tr>
                <tr>
                  <th>Money</th>
                  <td data-label="Cloudish">A prepaid balance on the key; the agent can't spend past it</td>
                  <td data-label="Vercel">A card on a team account, billed after usage</td>
                  <td data-label="Railway / Render / Fly">A card on file, billed after usage</td>
                  <td data-label="AWS / GCP">A billing account, billed after usage, no cap</td>
                </tr>
                <tr>
                  <th>Surface</th>
                  <td data-label="Cloudish">skill.md and the API; the dashboard is optional</td>
                  <td data-label="Vercel">The dashboard; domains and env vars live there</td>
                  <td data-label="Railway / Render / Fly">The dashboard and CLI</td>
                  <td data-label="AWS / GCP">The console</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------- datacenters */}
      <section className="section" id="datacenters" style={{ paddingTop: 0 }}>
        <div className="section-narrow" style={{ maxWidth: 640 }}>
          <div className="section-header">
            <span className="eyebrow">Infrastructure</span>
            <h2>Datacenters you can actually see</h2>
            <p className="muted">
              Today, do.cloudish.ai is the only datacenter live in production, running in New York.
              A second, in Seattle, is in testing. Each datacenter
              answers directly at its own subdomain instead of hiding behind an opaque region
              code, so an agent always knows exactly where its container runs and can target a
              specific one on purpose. A platform this small, containers, a registry, a ledger,
              and nothing else, is also fast to stand up again elsewhere. More cities are next,
              continents follow, then orbit stops being a joke.
            </p>
          </div>
          <div className="dc-list">
            <div className="dc-item">
              <span className="dc-dot dc-dot-live" />
              <span className="dc-name">do.cloudish.ai</span>
              <span className="dc-city">New York</span>
              <span className="badge badge-ok">live</span>
            </div>
            <div className="dc-item">
              <span className="dc-dot dc-dot-testing" />
              <span className="dc-name">seattle.cloudish.ai</span>
              <span className="dc-city">Seattle</span>
              <span className="badge">testing</span>
            </div>
            <div className="dc-item">
              <span className="dc-dot dc-dot-orbit" />
              <span className="dc-name">More cities</span>
              <span className="dc-city">TBD</span>
              <span className="badge">planning</span>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------------- faq */}
      <section className="section" id="faq" style={{ paddingTop: 0 }}>
        <div className="section-narrow" style={{ maxWidth: 640 }}>
          <div className="section-header">
            <span className="eyebrow">FAQ</span>
            <h2>What agents and their humans ask first</h2>
          </div>
          <div className="faq-list">
            {FAQ.map((f) => (
              <div className="faq-item" key={f.question}>
                <h3>{f.question}</h3>
                <p>{withLinks(f.answer, f.links)}</p>
              </div>
            ))}
          </div>
          {/* The same entries as schema.org FAQPage, for search engines and
              LLM crawlers (see content/faq.ts). */}
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqJsonLd() }} />
        </div>
      </section>

      {/* --------------------------------------------------------------- cta */}
      <section className="section">
        <div className="section-narrow cta-band">
          <h2>Get your first container running today</h2>
          <p>
            Free to start. Paste this into Claude: a SQLite-backed app that writes to its own
            disk, exactly the workload a serverless platform can't keep running.
          </p>
          <CopyBox value={CLAUDE_PROMPT} />
          <div className="cta-band-actions">
            <button type="button" className="btn btn-lg" onClick={() => login('/claim')}>
              Sign in to add credits
            </button>
            <a href="/#agents" className="btn btn-lg">
              Install the skill
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
